"use client";

import { useEffect, useRef, useState } from "react";
import { motion, useInView, useReducedMotion } from "motion/react";
import type { Variants } from "motion/react";
import { AedSymbol } from "@/components/ui/AedSymbol";

// ── Data ────────────────────────────────────────────────────────────────────

interface Stat {
  to: number;
  decimals?: number;
  prefix?: React.ReactNode;
  suffix?: string;
  label: string;
}

const STATS: Stat[] = [
  {
    to: 21,
    prefix: <AedSymbol className="h-[0.72em] w-[0.72em]" label="AED " />,
    suffix: "M+",
    label: "In gold and silver sold",
  },
  {
    to: 50000,
    suffix: "+",
    label: "Savers across the UAE",
  },
  {
    to: 24,
    suffix: "K",
    label: "Certified 999.9 pure gold",
  },
  {
    to: 100,
    suffix: "%",
    label: "Insured and Shariah compliant",
  },
];

// ── Motion ──────────────────────────────────────────────────────────────────

const container: Variants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.1, delayChildren: 0.05 } },
};

function fadeUp(reduce: boolean): Variants {
  return {
    hidden: { opacity: 0, y: reduce ? 0 : 18 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: [0.2, 0, 0, 1] },
    },
  };
}

// ── Count up ────────────────────────────────────────────────────────────────

function formatValue(value: number, decimals: number) {
  return value.toLocaleString("en-US", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

function CountUp({
  to,
  decimals = 0,
  active,
}: {
  to: number;
  decimals?: number;
  active: boolean;
}) {
  const reduce = useReducedMotion();
  const [value, setValue] = useState(reduce ? to : 0);

  useEffect(() => {
    if (!active) return;
    if (reduce) {
      setValue(to);
      return;
    }

    let frame = 0;
    const duration = 1400;
    let start: number | null = null;

    const tick = (now: number) => {
      if (start === null) start = now;
      const progress = Math.min((now - start) / duration, 1);
      // easeOutCubic
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(to * eased);
      if (progress < 1) {
        frame = requestAnimationFrame(tick);
      } else {
        setValue(to);
      }
    };

    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [active, to, reduce]);

  return <>{formatValue(value, decimals)}</>;
}

// ── Section ─────────────────────────────────────────────────────────────────

export function AboutStats() {
  const reduce = useReducedMotion() ?? false;
  const ref = useRef<HTMLElement>(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });

  return (
    <section
      ref={ref}
      className="border-y border-[#e3ecfb] bg-neutral-0"
      aria-label="JustGold by the numbers"
    >
      <motion.div
        variants={container}
        initial="hidden"
        animate={inView ? "visible" : "hidden"}
        className="mx-auto grid max-w-[1440px] grid-cols-2 gap-y-10 px-6 py-14 sm:px-10 lg:grid-cols-4 lg:gap-y-0 lg:px-20 lg:py-16"
      >
        {STATS.map((stat, i) => (
          <motion.div
            key={stat.label}
            variants={fadeUp(reduce)}
            className={
              i < STATS.length - 1
                ? "flex flex-col items-center text-center lg:border-r lg:border-primary-200/50"
                : "flex flex-col items-center text-center"
            }
          >
            <p className="font-heading text-[clamp(2rem,4vw,2.75rem)] font-bold leading-none tracking-[-0.02em] text-primary-900 tabular-nums">
              <span className="inline-flex items-baseline">
                {stat.prefix}
                <CountUp to={stat.to} decimals={stat.decimals} active={inView} />
                {stat.suffix}
              </span>
            </p>
            <p className="mt-3 max-w-[180px] text-sm leading-snug text-text-muted">
              {stat.label}
            </p>
          </motion.div>
        ))}
      </motion.div>
    </section>
  );
}
